import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Camera, Trash2 } from "lucide-react";
import { api } from "../api/client";
import { Button, Field, IconButton } from "../components/UIKit";
import { pickImage } from "../lib/imagePicker";

export default function VehicleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initial, setInitial] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoTouched, setPhotoTouched] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [plate, setPlate] = useState("");
  const [mileage, setMileage] = useState("");
  const [usageProfile, setUsageProfile] = useState("particulier");
  const [fuelType, setFuelType] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/vehicles/${id}`);
        setInitial(data);
        setPhoto(data.photo || null);
        setBrand(data.brand || "");
        setModel(data.model || "");
        setYear(data.year ? String(data.year) : "");
        setPlate(data.plate || "");
        setMileage(data.mileage != null ? String(data.mileage) : "");
        setUsageProfile(data.usage_profile || "particulier");
        setFuelType(data.fuel_type || "");
        setPurchasePrice(data.purchase_price != null ? String(data.purchase_price) : "");
        setPurchaseDate(data.purchase_date ? data.purchase_date.slice(0, 10) : "");
      } catch (e) {
        setFetchError("Véhicule introuvable");
      }
    })();
  }, [id]);

  const onPickPhoto = async () => {
    if (photoUploading) return;
    setPhotoUploading(true);
    try {
      const img = await pickImage({ aspect: [4, 3] });
      if (img) {
        setPhoto(img);
        setPhotoTouched(true);
      }
    } finally {
      setPhotoUploading(false);
    }
  };

  const onRemovePhoto = () => {
    setPhoto(null);
    setPhotoTouched(true);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!brand.trim() || !model.trim() || !plate.trim()) {
      setError("Marque, modèle et immatriculation requis");
      return;
    }
    // Build partial payload with only fields that actually changed.
    const payload = {};
    if (brand.trim() !== initial.brand) payload.brand = brand.trim();
    if (model.trim() !== initial.model) payload.model = model.trim();
    const trimmedPlate = plate.trim().toUpperCase();
    if (trimmedPlate !== (initial.plate || "").toUpperCase()) payload.plate = trimmedPlate;
    const yearInt = year ? parseInt(year) : null;
    if (yearInt !== (initial.year ?? null)) payload.year = yearInt;
    const kmInt = mileage ? parseInt(mileage) : null;
    if (kmInt !== null && kmInt !== initial.mileage) payload.mileage = kmInt;
    if (usageProfile && usageProfile !== (initial.usage_profile || "particulier")) payload.usage_profile = usageProfile;
    // Fuel type / purchase anchor (Phase 8). Empty string means "clear the field".
    const initFuel = initial.fuel_type || "";
    if (fuelType !== initFuel) payload.fuel_type = fuelType || null;
    const priceFloat = purchasePrice === "" ? null : Number(purchasePrice);
    const initPrice = initial.purchase_price ?? null;
    if (priceFloat !== initPrice) payload.purchase_price = priceFloat;
    const dateISO = purchaseDate ? new Date(purchaseDate).toISOString() : null;
    const initDate = initial.purchase_date || null;
    if (dateISO !== initDate) payload.purchase_date = dateISO;
    if (photoTouched) payload.photo = photo;

    if (Object.keys(payload).length === 0) {
      toast.info("Aucune modification");
      navigate(-1);
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/vehicles/${id}`, payload);
      toast.success("Véhicule mis à jour");
      navigate(`/vehicles/${id}`, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Impossible d'enregistrer les modifications."
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchError) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "var(--mf-text-secondary)" }}>
        {fetchError}
      </div>
    );
  }
  if (!initial) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <div style={{
          display: "inline-block", width: 24, height: 24, borderRadius: 12,
          border: "3px solid var(--mf-border)", borderTopColor: "var(--mf-brand)",
          animation: "mf-spin 700ms linear infinite",
        }} />
        <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 12px" }}>
        <IconButton testID="edit-vehicle-back" icon={<X size={22} />} onClick={() => navigate(-1)} ariaLabel="Fermer" />
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Modifier le véhicule</h2>
        <div style={{ width: 40 }} />
      </div>

      <form onSubmit={onSubmit} style={{ padding: "0 20px" }}>
        <div style={{ position: "relative", marginBottom: 20 }}>
          <button
            type="button"
            onClick={onPickPhoto}
            disabled={photoUploading}
            data-testid="edit-vehicle-photo"
            style={{
              width: "100%", height: 200, borderRadius: 20,
              background: "var(--mf-bg-card)", border: photo ? "none" : "2px dashed var(--mf-border)",
              display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6,
              overflow: "hidden", cursor: photoUploading ? "wait" : "pointer",
              boxShadow: photo ? "var(--mf-shadow)" : "none",
            }}
          >
            {photoUploading ? (
              <div style={{
                width: 32, height: 32, borderRadius: 16,
                border: "3px solid rgba(11,25,44,0.15)", borderTopColor: "var(--mf-brand)",
                animation: "mf-spin 700ms linear infinite",
              }} />
            ) : photo ? (
              <img src={photo} alt="Véhicule" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <>
                <span style={{
                  width: 48, height: 48, borderRadius: 14, background: "rgba(11,25,44,0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4,
                }}><Camera size={26} color="var(--mf-brand)" /></span>
                <span style={{ color: "var(--mf-text)", fontSize: 15, fontWeight: 600 }}>Ajouter une photo</span>
                <span style={{ color: "var(--mf-text-muted)", fontSize: 12 }}>Optionnel</span>
              </>
            )}
          </button>
          {photo && !photoUploading && (
            <button
              type="button"
              onClick={onRemovePhoto}
              data-testid="edit-vehicle-photo-remove"
              aria-label="Supprimer la photo"
              style={{
                position: "absolute", top: 12, right: 12, width: 36, height: 36, borderRadius: 18,
                background: "rgba(11,25,44,0.65)", color: "#fff", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
        <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>

        <Field testID="edit-vehicle-brand" label="Marque" value={brand} onChange={setBrand} placeholder="Renault, Peugeot..." />
        <Field testID="edit-vehicle-model" label="Modèle" value={model} onChange={setModel} placeholder="Clio, 308..." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12 }}>
          <Field testID="edit-vehicle-year" label="Année" value={year} onChange={setYear} placeholder="2022" maxLength={4} />
          <Field testID="edit-vehicle-plate" label="Immatriculation" value={plate} onChange={(v) => setPlate(v.toUpperCase())} placeholder="AB-123-CD" />
        </div>
        <Field
          testID="edit-vehicle-mileage"
          label="Kilométrage"
          value={mileage}
          onChange={setMileage}
          placeholder="0"
          hint={`Actuel · ${(initial.mileage ?? 0).toLocaleString("fr-FR")} km. Le kilométrage ne peut pas être réduit.`}
        />

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
            Profil d&apos;utilisation
          </label>
          <select
            value={usageProfile}
            onChange={(e) => setUsageProfile(e.target.value)}
            data-testid="edit-vehicle-usage-profile"
            style={{
              width: "100%", padding: "12px 14px", borderRadius: 12,
              background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
              color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
              appearance: "none",
            }}
          >
            <option value="particulier">Particulier</option>
            <option value="vtc">VTC</option>
            <option value="taxi">Taxi</option>
            <option value="livraison">Livraison</option>
            <option value="utilitaire">Utilitaire</option>
            <option value="personnalise">Personnalisé</option>
          </select>
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--mf-text-muted)" }}>
            Aide le moteur intelligent à estimer votre rythme d&apos;utilisation. Les intervalles restent personnalisables.
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
            Motorisation <span style={{ color: "var(--mf-text-muted)", fontWeight: 400 }}>(optionnel — utilisé pour l&apos;estimation de valeur)</span>
          </label>
          <select
            value={fuelType}
            onChange={(e) => setFuelType(e.target.value)}
            data-testid="edit-vehicle-fuel-type"
            style={{
              width: "100%", padding: "12px 14px", borderRadius: 12,
              background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
              color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
              appearance: "none",
            }}
          >
            <option value="">Non renseignée</option>
            <option value="essence">Essence</option>
            <option value="diesel">Diesel</option>
            <option value="hybride">Hybride</option>
            <option value="electrique">Électrique</option>
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field
            testID="edit-vehicle-purchase-price"
            label="Prix d'achat (€)"
            value={purchasePrice}
            onChange={setPurchasePrice}
            placeholder="Optionnel"
            hint="Facultatif — sert à comparer avec l'estimation de valeur."
          />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
              Date d&apos;achat
            </label>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              data-testid="edit-vehicle-purchase-date"
              style={{
                width: "100%", padding: "12px 14px", borderRadius: 12,
                background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
              }}
            />
          </div>
        </div>

        {error && <div style={{ color: "var(--mf-urgent-text)", textAlign: "center", marginTop: 8, marginBottom: 8, fontSize: 14 }}>{error}</div>}

        <Button testID="edit-vehicle-submit" type="submit" loading={loading} style={{ marginTop: 16 }}>
          Enregistrer
        </Button>
      </form>
    </div>
  );
}
